import React, { useEffect, useMemo, useRef, useState } from "react";
import * as Mui from "@mui/material";
import { useSelector } from "react-redux";
import { Add, AssignmentTurnedInOutlined, Close, ErrorOutline, TrendingUp } from "@mui/icons-material";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { PrimaryButton, SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { designColors, designRadii, designType, designWeights } from "services/constants/designTokens";
import { panelBodySx } from "pages/PayRoll/PayInputs/Shared/formUi";
import { SelectField, toOptions } from "pages/PayRoll/PayInputs/Shared/fieldInputs";
import {
  EmptyRow,
  FormField as Field,
  MethodCards,
  PanelFooter,
  PanelTitleBlock,
  Required,
  SalarySection,
  SimpleTable,
  SummaryStrip,
  ToneChip,
  VersionChip,
  dangerButtonSx,
  multilineFieldSx,
  tableCellSx,
  tableHeadSx,
  useSalaryPanelTrail,
} from "../Shared/salaryUi";
import { CheckLine, inputSx, totalRows } from "../SalaryTemplates/SalaryTemplatePanels";
import {
  CATEGORY_NOUNS,
  TEMPLATE_CATEGORIES,
  currentTemplateVersion,
  evaluateTemplate,
  lineCategory,
  lineName,
  withBalancing,
} from "../SalaryTemplates/salaryTemplateData";
import { ApprovalSteps, CustomizeStructurePanel } from "../AssignSalary/AssignSalaryPanels";
import { BulkReprocessEmployeePicker } from "pages/HR/EmployeeOnboard/BulkReprocessEmployeePicker";
import { getTemplateSalaryType, listGroupTemplates, listPayrollGroups, listSalaryTypeUsers } from "./revisionApi";
import { SALARY_TYPES, formatStamp } from "../AssignSalary/assignSalaryData";
import {
  CHANGE_OPTIONS,
  PERIOD,
  REASONS,
  STRUCTURE_OPTIONS,
  TEMPLATE_CHANGE_OPTIONS,
  blockReason,
  byVersion,
  versionText,
  changeText,
  checkRevisionForm,
  currentLines,
  employeeLabel,
  employeeOfList,
  formatDay,
  formatRupees,
  isCustomized,
  linesFor,
  rowLines,
  openRevisionByEmployee,
  requestStatus,
  rowNewGross,
  structureLabel,
  targetFor,
  templateById,
  versionChanges,
  versionLabel,
  versionOf,
  withTemplateRows,
} from "./revisionData";

const SCREEN = "Salary Revision";
const ROW_LIMIT = 5;
const NO_REVISIONS = [];
const money = (value) => (value == null || Number.isNaN(Number(value)) ? "–" : formatRupees(value));
const plain = (value) => (value == null ? "—" : Math.round(Number(value)).toLocaleString("en-IN"));
const pct = (from, to) => (from ? `${to >= from ? "+" : ""}${Math.round(((to - from) / from) * 1000) / 10}%` : "–");
const sameId = (a, b) => a != null && b != null && String(a) === String(b);
const deleteButtonSx = { color: "#B42318", borderColor: "#FCA5A5", "&:hover": { borderColor: "#B42318", backgroundColor: "#FEF2F2" } };
const warning = (text) => (
  <Mui.Stack direction="row" spacing={0.75} sx={{ color: "#B54708" }}>
    <ErrorOutline sx={{ fontSize: 18 }} />
    <Mui.Typography sx={{ fontSize: 14, fontWeight: designWeights.semibold }}>{text}</Mui.Typography>
  </Mui.Stack>
);

// ---------------- Current vs new breakdown ----------------

// beforeResult / afterResult: the server's amounts, used instead of working
// them out from the rows. A total the server leaves out is worked out here.
const withLocal = (server, local) =>
  server ? { ...local, ...Object.fromEntries(Object.entries(server).filter(([, value]) => value != null)) } : local;

export const CompareBreakdown = ({ catalog, beforeLines, beforeGross, afterLines, afterGross, beforeResult, afterResult, beforeLabel = "Current", afterLabel = "New" }) => {
  const before = withLocal(beforeResult, evaluateTemplate(catalog, beforeLines, beforeGross));
  const after = withLocal(afterResult, evaluateTemplate(catalog, afterLines, afterGross));
  const all = [...withBalancing(catalog, afterLines), ...withBalancing(catalog, beforeLines)];
  const names = Object.fromEntries(all.map((line) => [line.code, lineName(catalog, line)]));
  const codes = (category) => [...new Set(all.filter((line) => lineCategory(catalog, line) === category).map((line) => line.code))];
  const row = (label, a, b, strong) => (
    <Mui.Stack key={label} direction="row" spacing={1.5} alignItems="center" sx={{ py: 0.6 }}>
      <Mui.Typography sx={{ fontSize: 14, flex: 1, color: designColors.slate700, fontWeight: strong ? designWeights.heading : 400 }}>{label}</Mui.Typography>
      <Mui.Typography sx={{ fontSize: 14, width: 72, textAlign: "right", color: designColors.slate500 }}>{plain(a)}</Mui.Typography>
      <Mui.Typography sx={{ fontSize: 14, width: 80, textAlign: "right", color: designColors.slate900, fontWeight: strong ? designWeights.heading : designWeights.medium }}>{strong ? money(b) : plain(b)}</Mui.Typography>
    </Mui.Stack>
  );
  const heading = (label, showCols) => (
    <Mui.Stack direction="row" spacing={1.5} sx={{ mt: 1.25, mb: 0.25 }}>
      <Mui.Typography sx={{ ...designType.captionText, flex: 1, letterSpacing: "0.04em", fontWeight: designWeights.semibold }}>{label}</Mui.Typography>
      {showCols ? (
        <>
          <Mui.Typography sx={{ ...designType.captionText, width: 72, textAlign: "right", fontWeight: designWeights.semibold }}>{beforeLabel}</Mui.Typography>
          <Mui.Typography sx={{ ...designType.captionText, width: 80, textAlign: "right", fontWeight: designWeights.semibold }}>{afterLabel}</Mui.Typography>
        </>
      ) : null}
    </Mui.Stack>
  );
  const divider = <Mui.Box sx={{ borderTop: `1px solid ${designColors.slate200}`, my: 0.5 }} />;
  return (
    <Mui.Box>
      {TEMPLATE_CATEGORIES.map((category) =>
        category === "Earnings" || codes(category).length ? (
          <React.Fragment key={category}>
            {heading(`${CATEGORY_NOUNS[category][1].toUpperCase()}${category === "Calculation" ? " · NOT PAID" : ""}`, category === "Earnings")}
            {codes(category).map((code) => row(names[code] || code, before.values?.[code], after.values?.[code]))}
          </React.Fragment>
        ) : null,
      )}
      {divider}
      {totalRows(catalog, all).map(([label, key, strong]) => row(label, before[key], after[key], strong))}
    </Mui.Box>
  );
};


// ---------------- Select employees ----------------

// Add employees: the People > Bulk Reprocess picker as it is (By Employee /
// By Department / Advanced Filter), so employees are picked the same way
// across screens.
// - The list is salary-type-users for every salary type.
// - Advanced Filter (Department, Payroll Group, Template, Salary Type):
//   Payroll Group lists the company's payroll groups; Template lists the
//   picked groups' templates (payroll-group-templates); Salary Type lists
//   the picked templates' types (template-salary-type). With nothing picked
//   above, a field lists the values in the employee list.
// employees: revision-eligible-employees. A listed user is matched to it
// for the current salary; anyone not revisable (no active salary, an open
// revision) isn't listed.
const uniqueSorted = (values) => [...new Set(values.filter(Boolean))].sort((a, b) => a.localeCompare(b));

export const SelectEmployeesPanel = ({ open, employees, templates, loading, error, excludeIds, onClose, onAdd }) => {
  const trail = useSalaryPanelTrail(SCREEN, onClose);
  const { domain, topbarCompanyId: companyId } = useSelector((state) => state.tokenReducer);
  const [selected, setSelected] = useState([]);
  const [draft, setDraft] = useState({});
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState({ list: [], loading: false, error: "" });
  // Per payroll group id: its templates. Per template id: its salary type.
  const [templatesByGroup, setTemplatesByGroup] = useState({});
  const [typeByTemplate, setTypeByTemplate] = useState({});
  const requested = useRef({ groups: new Set(), templates: new Set() });

  // Fresh start on every open.
  useEffect(() => {
    if (!open) return;
    setSelected([]);
    setDraft({});
  }, [open]);

  // The company's payroll groups, and everyone on each salary type.
  useEffect(() => {
    if (!open || !companyId) return undefined;
    const controller = new AbortController();
    const { signal } = controller;
    listPayrollGroups(domain, companyId, signal)
      .then((list) => !signal.aborted && setGroups(list))
      .catch(() => {});
    setUsers({ list: [], loading: true, error: "" });
    // One salary type failing doesn't hide the others' employees.
    Promise.allSettled(
      SALARY_TYPES.map((type) =>
        listSalaryTypeUsers(domain, companyId, type, signal).then((list) =>
          list.map((user) => ({ ...user, salary_type: user.salary_type || type })),
        ),
      ),
    ).then((results) => {
      if (signal.aborted) return;
      const loaded = results.filter((result) => result.status === "fulfilled");
      if (!loaded.length) {
        setUsers({ list: [], loading: false, error: results[0]?.reason?.message || "Couldn't load employees." });
        return;
      }
      const seen = new Set();
      const list = loaded.flatMap((result) => result.value).filter((user) => !seen.has(String(user.id)) && seen.add(String(user.id)));
      setUsers({ list, loading: false, error: "" });
    });
    return () => controller.abort();
  }, [open, domain, companyId]);

  // Picked payroll groups -> their templates (payroll-group-templates).
  const pickedGroupIds = (draft.group || [])
    .map((name) => groups.find((item) => item.name === name)?.id)
    .filter(Boolean);
  const pickedGroupKey = pickedGroupIds.join(",");
  useEffect(() => {
    if (!open) return;
    pickedGroupKey.split(",").filter(Boolean).forEach((id) => {
      if (requested.current.groups.has(id)) return;
      requested.current.groups.add(id);
      listGroupTemplates(domain, id)
        .then((list) => setTemplatesByGroup((current) => ({ ...current, [id]: list })))
        .catch(() => requested.current.groups.delete(id));
    });
  }, [open, domain, pickedGroupKey]);
  const groupTemplateList = pickedGroupIds.flatMap((id) => templatesByGroup[id] || []);

  // Picked templates -> their salary types (template-salary-type).
  const pickedTemplateIds = (draft.template || [])
    .map((name) => groupTemplateList.find((item) => item.name === name)?.id)
    .filter(Boolean);
  const pickedTemplateKey = pickedTemplateIds.join(",");
  useEffect(() => {
    if (!open) return;
    pickedTemplateKey.split(",").filter(Boolean).forEach((id) => {
      if (requested.current.templates.has(id)) return;
      requested.current.templates.add(id);
      getTemplateSalaryType(domain, id)
        .then((type) => setTypeByTemplate((current) => ({ ...current, [id]: type })))
        .catch(() => requested.current.templates.delete(id));
    });
  }, [open, domain, pickedTemplateKey]);

  const noOpen = useMemo(() => new Map(), []);
  const excluded = new Set(excludeIds.map(String));
  // A listed user, with the current salary from revision-eligible-employees.
  const candidates = users.list
    .filter((user) => !excluded.has(String(user.id)))
    .map((user) => ({ user, employee: employees.find((item) => sameId(item.id, user.id)) }));
  const reasonFor = ({ employee }) => (employee ? blockReason(employee, noOpen) : "No active salary to revise");
  const pickerUsers = (loading ? [] : candidates.filter((item) => !reasonFor(item))).map(({ user, employee }) => {
    const template = templateById(templates, employee.template_id);
    return {
      user_id: employee.id,
      user_name: employee.name || user.name,
      employee_id: employee.code || user.code,
      department_name: employee.department || user.department || "No department",
      payroll_group: employee.group || user.group || "",
      template_name: template?.name || employee.template_name || user.template_name || "",
      salary_type: employee.salary_type || user.salary_type || "",
    };
  });

  const groupOptions = groups.length ? uniqueSorted(groups.map((item) => item.name)) : undefined;
  const templateOptions = pickedGroupIds.length ? uniqueSorted(groupTemplateList.map((item) => item.name)) : undefined;
  const typeOptions = pickedTemplateIds.length
    ? uniqueSorted(pickedTemplateIds.map((id) => typeByTemplate[id] || groupTemplateList.find((item) => item.id === id)?.salary_type))
    : undefined;
  const optionsKey = JSON.stringify([groupOptions, templateOptions, typeOptions]);
  const filterFields = useMemo(
    () => [
      { key: "group", label: "Payroll Group", allLabel: "All payroll groups", field: "payroll_group", options: groupOptions },
      { key: "template", label: "Template", allLabel: "All templates", field: "template_name", options: templateOptions },
      { key: "salary_type", label: "Salary Type", allLabel: "All salary types", field: "salary_type", options: typeOptions },
    ],
    // optionsKey stands for the three option lists.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [optionsKey],
  );


  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      hideExpand
      breadcrumb={[...trail, "Select employees"]}
      title="Select Employees"
      subtitle="Select employees individually, by department, or using an advanced filter."
      footer={
        <Mui.Stack direction="row" justifyContent="flex-end" alignItems="center">
          <PrimaryButton disabled={!selected.length} onClick={() => onAdd(selected.map((user) => user.user_id))}>
            Add Selected Employees
          </PrimaryButton>
        </Mui.Stack>
      }
    >
      <Mui.Box sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
        <BulkReprocessEmployeePicker
          users={pickerUsers}
          selected={selected}
          onChange={setSelected}
          loading={users.loading || loading}
          error={users.error || error}
          companySelected={Boolean(companyId)}
          emptySelectionText="Select employees to include in this revision."
          filterFields={filterFields}
          onFilterChange={setDraft}
        />
      </Mui.Box>
    </GlobalSidePanel>
  );
};

// ---------------- Revise form ----------------

export const RevisionFormPanel = ({
  open,
  initial,
  revision,
  catalog,
  templates,
  employees,
  templateEmployees,
  eligibleLoading,
  eligibleError,
  eligibleLoaded = true,
  reasons,
  minDate,
  lastProcessed,
  busy,
  expanded,
  onToggleExpand,
  onClose,
  onNeedEmployees,
  onTemplateEmployees,
  onTemplate,
  onSaveDraft,
  onSubmit,
  onDelete,
}) => {
  const trail = useSalaryPanelTrail(SCREEN, onClose, busy);
  const [form, setForm] = useState(initial);
  const [showErrors, setShowErrors] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [previewId, setPreviewId] = useState(null);
  const [selecting, setSelecting] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  // One employee's Customize (Assign Salary's per-employee link): a snapshot
  // { employee_id, gross, lines } taken when it opens.
  const [rowCustomize, setRowCustomize] = useState(null);
  useEffect(() => {
    if (!open) return;
    setForm(initial);
    setShowErrors(false);
    setShowAll(false);
    setSelecting(false);
    setCustomizing(false);
    setPreviewId(null);
  }, [open, initial]);

  const isTemplate = form?.kind === "template";
  const templateKey = form?.template_id ? String(form.template_id) : "";
  const templateList = isTemplate ? templateEmployees[templateKey] : null;
  // Template mode works on the template's employees; the other mode on the
  // eligible list (plus the request's own employees).
  const people = useMemo(() => {
    if (!isTemplate) return employees;
    const own = employees.filter((employee) => form?.rows.some((row) => sameId(row.employee_id, employee.id)));
    const ids = new Set((templateList || []).map((employee) => String(employee.id)));
    return [...(templateList || []), ...own.filter((employee) => !ids.has(String(employee.id)))];
  }, [isTemplate, employees, templateList, form?.rows]);

  // Loads: eligible list, the template's employees, the template's rows.
  useEffect(() => {
    if (open && form && !isTemplate) onNeedEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isTemplate]);
  useEffect(() => {
    if (!open || !templateKey) return;
    onTemplate(form.template_id);
    // templates.length: retry once the template list has arrived.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, templateKey, templates.length]);
  // The template's employees: fetched again on every template pick.
  useEffect(() => {
    if (open && isTemplate && templateKey) onTemplateEmployees(form.template_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, templateKey, isTemplate]);
  // Each picked employee's own template (rows for the preview and checks).
  const rowTemplateIds = [
    ...new Set((form?.rows || []).map((row) => employeeOfList(people, row.employee_id)?.template_id).filter(Boolean).map(String)),
  ].join(",");
  useEffect(() => {
    if (open) rowTemplateIds.split(",").filter(Boolean).forEach((templateId) => onTemplate(templateId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, rowTemplateIds, templates.length]);
  // Template mode: fill the rows once the template's employees arrive.
  useEffect(() => {
    if (!open || !isTemplate || !templateList || !templateById(templates, form.template_id)) return;
    if (form.rows_for === templateKey) return;
    setForm((current) => withTemplateRows(current, templates, templateList, NO_REVISIONS, current.revision_id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isTemplate, templateList, templateKey, templates, form?.rows_for]);

  const check = useMemo(
    () => (form ? checkRevisionForm(form, { catalog, templates, employees: people, revisions: NO_REVISIONS, minDate }) : null),
    [form, catalog, templates, people, minDate],
  );
  if (!form || !check) return null;

  // Until the employee list arrives (e.g. opened from Employee Salary), a
  // row shows "Loading..." instead of an id.
  const employeeOf = (id) =>
    employeeOfList(people, id) ||
    (eligibleLoading
      ? { id, name: "Loading...", code: "", department: "", gross: 0, eligible: true }
      : { id, name: `Employee ${id}`, code: "", department: "", gross: 0, eligible: false, reason: eligibleError ? "Employee details couldn't be loaded" : "Not found among employees with a salary" });
  const set = (change) => setForm((current) => ({ ...current, ...change }));
  const setRow = (id, change) => set({ rows: form.rows.map((row) => (sameId(row.employee_id, id) ? { ...row, ...change } : row)) });
  const template = templateById(templates, form.template_id);
  const templateLines = template ? currentTemplateVersion(template).lines || [] : [];
  const noOpen = openRevisionByEmployee(NO_REVISIONS);
  const rows = check.rows;
  const count = rows.length;
  const totals = rows.reduce(
    (acc, row) => {
      const employee = employeeOf(row.employee_id);
      return { current: acc.current + Number(employee.gross || 0), next: acc.next + rowNewGross(form, employee, row) };
    },
    { current: 0, next: 0 },
  );
  const previewRow = rows.find((row) => sameId(row.employee_id, previewId)) || rows[0];
  const previewEmployee = previewRow ? employeeOf(previewRow.employee_id) : null;
  const previewTarget = previewEmployee ? targetFor(form, templates, previewEmployee) : null;
  const blocking = Object.keys(check.errors).length || Object.keys(check.perEmployee).length;
  const period = PERIOD[template?.pay_basis || previewEmployee?.salary_type || templateById(templates, previewEmployee?.template_id)?.pay_basis] || "month";
  const reasonList = reasons?.length ? reasons : REASONS;
  const reasonOptions = toOptions(form.reason && !reasonList.includes(form.reason) ? [...reasonList, form.reason] : reasonList);

  const submit = () => {
    setShowErrors(true);
    if (blocking) return;
    onSubmit(form, check, people);
  };
  const saveDraft = () => {
    if (!check.rows.length) {
      setShowErrors(true);
      return;
    }
    onSaveDraft(form, check, people);
  };

  const changeField =
    form.change_mode === "percent" || form.change_mode === "fixed" ? (
      <Field label={<>{form.change_mode === "percent" ? "Increase (%)" : `Increase (₹ per ${period})`}<Required /></>} helper="Applied to each current gross; edit any row" error={showErrors ? check.errors.change_value : undefined}>
        <Mui.TextField
          size="small"
          type="number"
          value={form.change_value}
          disabled={busy}
          onChange={(event) => set({ change_value: event.target.value, rows: form.rows.map((row) => ({ ...row, manual: false })) })}
          InputProps={form.change_mode === "percent" ? { endAdornment: <Mui.InputAdornment position="end">%</Mui.InputAdornment> } : { startAdornment: <Mui.InputAdornment position="start">₹</Mui.InputAdornment> }}
          inputProps={{ "aria-label": "Increase", min: 0 }}
          sx={multilineFieldSx}
        />
      </Field>
    ) : null;

  const dateHelper = minDate
    ? `Payroll is processed up to ${formatDay(lastProcessed || minDate)}. The date can't be earlier than ${formatDay(minDate)}.`
    : "Must be after the last processed payroll. Back-dated revisions are refused.";
  const commonFields = (
    <>
      <Field label={<>Effective from<Required /></>} helper={dateHelper} error={showErrors ? check.errors.effective_from : undefined}>
        <Mui.TextField
          size="small"
          type="date"
          value={form.effective_from}
          disabled={busy}
          onChange={(event) => set({ effective_from: event.target.value })}
          inputProps={{ "aria-label": "Effective from", min: minDate || undefined }}
          sx={multilineFieldSx}
        />
      </Field>
      <Field label={<>Reason<Required /></>} error={showErrors ? check.errors.reason : undefined}>
        <SelectField value={form.reason} onChange={(value) => set({ reason: value })} options={reasonOptions} placeholder="Select reason" disabled={busy} />
      </Field>
      <Field label="Comments">
        <Mui.TextField size="small" value={form.comment} disabled={busy} onChange={(event) => set({ comment: event.target.value })} placeholder="Optional, shown to approvers" sx={multilineFieldSx} />
      </Field>
    </>
  );

  const templateOptions = templates.filter((item) => item.active).map((item) => ({ value: item.id, label: item.name }));
  if (form.template_id && !templateOptions.some((item) => sameId(item.value, form.template_id))) {
    const name = template?.name || revision?.template_name;
    if (name) templateOptions.push({ value: form.template_id, label: name });
  }

  const whatChanges = isTemplate ? (
    <SalarySection title="Template">
      <Mui.Stack spacing={2}>
        <Field
          label={<>Template<Required /></>}
          helper={template ? `${template.employee_count != null ? `${template.employee_count} employees · ` : ""}current version ${versionLabel(currentTemplateVersion(template).version)}` : null}
          error={showErrors ? check.errors.template : undefined}
        >
          <SelectField
            value={form.template_id}
            onChange={(value) => {
              const next = templates.find((item) => sameId(item.id, value));
              set({ template_id: value || null, target_version: next ? currentTemplateVersion(next).version : null, rows: [], from_versions: [], rows_for: null });
            }}
            options={templateOptions}
            placeholder="Select template"
            disabled={busy || Boolean(form.revision_id)}
          />
        </Field>
        {template ? (
          <Field label="Employees currently on">
            {!templateList ? (
              <Mui.Typography sx={designType.helperText}>Loading the template's employees...</Mui.Typography>
            ) : (
              <Mui.Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {(() => {
                  const onTemplate = people.filter((employee) => sameId(employee.template_id, template.id));
                  const known = [...template.versions.map((version) => version.version), ...onTemplate.map((employee) => employee.version).filter((version) => version != null)];
                  const unknown = onTemplate.some((employee) => employee.version == null) ? [null] : [];
                  return [...new Set([...known, ...unknown])].sort(byVersion);
                })().map((version) => {
                    const onIt = people.filter((employee) => sameId(employee.template_id, template.id) && (employee.version ?? null) === version).length;
                    // Newer than the target can't be moved back; the target's
                    // own employees can still take the salary change.
                    const isNewer = version != null && version > form.target_version;
                    const isTarget = version === form.target_version;
                    const available = form.rows.some((row) => (employeeOf(row.employee_id).version ?? null) === version);
                    const checked = form.from_versions.includes(version) && !isNewer;
                    const count = `${onIt} employee${onIt === 1 ? "" : "s"}`;
                    return (
                      <Mui.FormControlLabel
                        key={version ?? "unknown"}
                        disabled={busy || isNewer || !available}
                        control={<Mui.Checkbox size="small" checked={checked} onChange={() => set({ from_versions: checked ? form.from_versions.filter((item) => item !== version) : [...form.from_versions, version] })} sx={{ "&.Mui-checked": { color: designColors.tealDark } }} />}
                        label={isNewer ? `${versionLabel(version)} · newer than target` : isTarget ? `${versionLabel(version)} · already current · ${count}` : `${versionText(version)} · ${count}`}
                        sx={{ m: 0, pr: 1.5, border: `1px solid ${checked ? "#A7E3CB" : designColors.slate200}`, backgroundColor: checked ? "#ECFDF3" : designColors.white, borderRadius: designRadii.card, "& .MuiFormControlLabel-label": { fontSize: 14, fontWeight: designWeights.semibold } }}
                      />
                    );
                  })}
              </Mui.Stack>
            )}
          </Field>
        ) : null}
        {/* The version employees move to; the latest by default. Picking
            another rebuilds the employee list for it. */}
        <Field label={<>Move to<Required /></>} helper="Template version the selected employees move to.">
          <SelectField
            value={form.target_version}
            onChange={(value) => {
              if (value == null || value === "" || value === form.target_version) return;
              set({ target_version: value, from_versions: [], rows: [], rows_for: null });
            }}
            options={(template?.versions || [])
              .map((item) => item.version)
              .filter((version) => version != null)
              .sort((x, y) => y - x)
              .map((version) => ({
                value: version,
                label: `${versionLabel(version)}${template && version === currentTemplateVersion(template).version ? " (latest)" : ""}`,
              }))}
            placeholder={template ? "Select version" : "Select a template first"}
            disabled={busy || !template}
          />
        </Field>
        {commonFields}
        <Field label="Salary change">
          <MethodCards value={form.change_mode} options={TEMPLATE_CHANGE_OPTIONS} onChange={(value) => set({ change_mode: value, rows: form.rows.map((row) => ({ ...row, manual: false })) })} />
        </Field>
        {changeField}
      </Mui.Stack>
    </SalarySection>
  ) : (
    <SalarySection title="What changes" action={<Mui.Typography sx={designType.captionText}>Applies to everyone below</Mui.Typography>}>
      <Mui.Stack spacing={2}>
        <Field label="Structure">
          <MethodCards value={form.structure} options={STRUCTURE_OPTIONS} onChange={(value) => set({ structure: value })} />
        </Field>
        {form.structure !== "keep" ? (
          <Field label={<>{form.structure === "customize" ? "Based on template" : "New template"}<Required /></>} helper="Customized employees lose their customization" error={showErrors ? check.errors.template : undefined}>
            <SelectField value={form.template_id} onChange={(value) => set({ template_id: value || null, common_lines: null })} options={templateOptions} placeholder="Select template" disabled={busy} />
          </Field>
        ) : null}
        {form.structure === "customize" && template ? (
          <Mui.Stack direction="row" spacing={1.5} alignItems="center">
            <SecondaryButton onClick={() => setCustomizing(true)} disabled={busy || !templateLines.length} sx={{ minWidth: 0 }}>
              {!templateLines.length ? "Loading the template..." : form.common_lines ? "Edit common structure" : "Set up common structure"}
            </SecondaryButton>
            {showErrors && check.errors.common ? <Mui.Typography sx={{ ...designType.captionText, color: "#DC2626" }}>{check.errors.common}</Mui.Typography> : null}
          </Mui.Stack>
        ) : null}
        <Field label="Salary change">
          <MethodCards value={form.change_mode} options={CHANGE_OPTIONS} onChange={(value) => set({ change_mode: value, change_value: value === "percent" || value === "fixed" ? form.change_value : "", rows: form.rows.map((row) => ({ ...row, manual: false })) })} />
        </Field>
        {changeField}
        {commonFields}
      </Mui.Stack>
    </SalarySection>
  );

  const grossInput = (employee, row) => (
    <Mui.TextField
      size="small"
      type="number"
      value={row.manual || form.change_mode === "set" ? row.new_gross || String(employee.gross ?? "") : String(rowNewGross(form, employee, row))}
      disabled={busy || form.change_mode === "none"}
      onChange={(event) => setRow(row.employee_id, { new_gross: event.target.value, manual: true })}
      InputProps={{ startAdornment: <Mui.InputAdornment position="start">₹</Mui.InputAdornment> }}
      inputProps={{ "aria-label": `${employee.name} new gross`, min: 0, style: { textAlign: "right" } }}
      sx={inputSx}
    />
  );
  const problemsLine = (row) =>
    showErrors && check.perEmployee[row.employee_id] ? <Mui.Typography sx={{ ...designType.captionText, color: "#DC2626", mt: 0.25 }}>{check.perEmployee[row.employee_id].join(" · ")}</Mui.Typography> : null;

  let employeesSection;
  if (isTemplate) {
    const visible = showAll ? form.rows : form.rows.slice(0, 7);
    const groups = [...new Set(visible.map((row) => employeeOf(row.employee_id).version ?? null))].sort(byVersion);
    employeesSection = (
      <SalarySection title={`Employees · ${count} of ${form.rows.length} selected`} action={<Mui.Typography sx={designType.captionText}>Template Linked are ticked · Customized are not</Mui.Typography>}>
        <Mui.Box sx={{ mx: -2, mt: -1.75, overflowX: "auto" }}>
          <Mui.Table size="small" sx={{ minWidth: 780 }}>
            <Mui.TableHead>
              <Mui.TableRow sx={{ backgroundColor: designColors.slateBg }}>
                {["", "Employee", "Version", "Structure", `Gross / ${period}`, ""].map((head, index) => (
                  <Mui.TableCell key={`${head}-${index}`} align={index === 4 ? "right" : "left"} sx={{ ...tableHeadSx, width: [44, undefined, 120, 150, 150, 140][index] }}>{head}</Mui.TableCell>
                ))}
              </Mui.TableRow>
            </Mui.TableHead>
            <Mui.TableBody>
              {groups.map((version) => {
                const inGroup = visible.filter((row) => (employeeOf(row.employee_id).version ?? null) === version);
                const all = form.rows.filter((row) => (employeeOf(row.employee_id).version ?? null) === version);
                return (
                  <React.Fragment key={version ?? "none"}>
                    <Mui.TableRow sx={{ backgroundColor: designColors.slateBg }}>
                      <Mui.TableCell colSpan={6} sx={{ ...tableCellSx, fontWeight: designWeights.semibold, color: form.from_versions.includes(version) ? designColors.slate700 : designColors.slate400 }}>
                        On {versionText(version)} · {all.length} employee{all.length === 1 ? "" : "s"} · {all.filter((row) => row.selected).length} selected{form.from_versions.includes(version) ? "" : " · version not included"}
                      </Mui.TableCell>
                    </Mui.TableRow>
                    {inGroup.map((row) => {
                      const employee = employeeOf(row.employee_id);
                      const block = blockReason(employee, noOpen);
                      const dim = Boolean(block) || !row.selected;
                      return (
                        <Mui.TableRow key={row.employee_id} onClick={() => setPreviewId(row.employee_id)} sx={{ cursor: "pointer", backgroundColor: previewRow && sameId(previewRow.employee_id, row.employee_id) ? designColors.tealTint : undefined }}>
                          <Mui.TableCell sx={tableCellSx} onClick={(event) => event.stopPropagation()}>
                            <Mui.Checkbox size="small" disabled={busy || Boolean(block)} checked={row.selected && !block} onChange={() => setRow(row.employee_id, { selected: !row.selected })} inputProps={{ "aria-label": `Select ${employee.name}` }} sx={{ p: 0.5, "&.Mui-checked": { color: designColors.tealDark } }} />
                          </Mui.TableCell>
                          <Mui.TableCell sx={tableCellSx}>
                            <Mui.Typography sx={{ fontSize: 14, fontWeight: designWeights.semibold, color: dim ? designColors.slate500 : designColors.slate900 }}>{employee.name}</Mui.Typography>
                            <Mui.Typography sx={designType.captionText}>{[employee.code, employee.department].filter(Boolean).join(" · ")}</Mui.Typography>
                            {block ? <Mui.Typography sx={{ ...designType.captionText, color: "#B54708" }}>{block}, can't be added</Mui.Typography> : null}
                            {!block && isCustomized(employee) ? <Mui.Typography sx={{ ...designType.captionText, color: "#B54708" }}>Customized: moving to {versionLabel(form.target_version)} replaces their changes</Mui.Typography> : null}
                            {problemsLine(row)}
                          </Mui.TableCell>
                          <Mui.TableCell sx={tableCellSx}>
                            <Mui.Stack direction="row" spacing={0.5} alignItems="center"><VersionChip label={employee.version == null ? "V?" : versionLabel(employee.version)} /><span>→</span><VersionChip label={versionLabel(form.target_version)} current /></Mui.Stack>
                          </Mui.TableCell>
                          <Mui.TableCell sx={tableCellSx}>
                            <ToneChip
                              label={row.lines ? "Customized · edited" : isCustomized(employee) ? "Customized" : "Template Linked"}
                              tone={row.lines || isCustomized(employee) ? "purple" : "slate"}
                            />
                          </Mui.TableCell>
                          <Mui.TableCell align="right" sx={tableCellSx} onClick={(event) => event.stopPropagation()}>
                            {form.change_mode === "none" && !row.manual ? <Mui.Box sx={{ color: dim ? designColors.slate500 : designColors.slate900 }}>{money(employee.gross)}</Mui.Box> : grossInput(employee, row)}
                          </Mui.TableCell>
                          {/* Same per-employee Customize as Select employees:
                              this employee's rows on the target version. */}
                          <Mui.TableCell sx={tableCellSx} onClick={(event) => event.stopPropagation()}>
                            {(() => {
                              const next = rowNewGross(form, employee, row);
                              const target = targetFor(form, templates, employee);
                              const ready = next > 0 && templateById(templates, target.template_id) && versionOf(templates, target.template_id, target.version);
                              return (
                                <Mui.Link
                                  component="button"
                                  type="button"
                                  underline="hover"
                                  disabled={busy || Boolean(block) || !row.selected || !ready}
                                  onClick={() => setRowCustomize({ employee_id: row.employee_id, gross: next, lines: rowLines(catalog, templates, form, employee, row, next) })}
                                  sx={{ fontSize: 14, fontWeight: designWeights.semibold, whiteSpace: "nowrap", color: designColors.tealDark, "&:disabled": { color: designColors.slate400, cursor: "default" } }}
                                >
                                  {row.lines ? "Edit structure" : "Customize"}
                                </Mui.Link>
                              );
                            })()}
                          </Mui.TableCell>
                        </Mui.TableRow>
                      );
                    })}
                  </React.Fragment>
                );
              })}
              {!form.rows.length ? (
                <EmptyRow colSpan={6} text={!template ? "Select a template." : !templateList ? "Loading the template's employees..." : "No employees on this template up to the selected version."} />
              ) : null}
            </Mui.TableBody>
          </Mui.Table>
        </Mui.Box>
        <Mui.Typography sx={{ ...designType.captionText, mt: 1.5 }}>
          Showing {visible.length} of {form.rows.length}
          {form.rows.length > 7 ? (
            <> · <Mui.Link component="button" type="button" underline="hover" onClick={() => setShowAll((value) => !value)} sx={{ fontSize: 12, fontWeight: designWeights.semibold, color: designColors.tealDark }}>{showAll ? "Show less" : "Show all"}</Mui.Link></>
          ) : null}
          {" "}· Ticking a Customized employee replaces their changes with {versionLabel(form.target_version)}.
        </Mui.Typography>
        {showErrors && check.errors.rows ? <Mui.Typography sx={{ ...designType.captionText, color: "#DC2626", mt: 1 }}>{check.errors.rows}</Mui.Typography> : null}
      </SalarySection>
    );
  } else {
    const visible = showAll ? form.rows : form.rows.slice(0, ROW_LIMIT);
    employeesSection = (
      <SalarySection
        title={`Employees · ${form.rows.length}`}
        action={
          <Mui.Link component="button" type="button" underline="hover" disabled={busy} onClick={() => {
            // The picker needs the eligible list (current salaries) in every
            // mode, incl. From a template; it's loaded once per form session.
            onNeedEmployees();
            setSelecting(true);
          }} sx={{ fontSize: 14, fontWeight: designWeights.semibold, color: designColors.tealDark, display: "inline-flex", alignItems: "center", gap: 0.5 }}>
            <Add sx={{ fontSize: 18 }} /> Add employees
          </Mui.Link>
        }
      >
        <Mui.Box sx={{ mx: -2, mt: -1.75, overflowX: "auto" }}>
          <Mui.Table size="small" sx={{ minWidth: 800 }}>
            <Mui.TableHead>
              <Mui.TableRow sx={{ backgroundColor: designColors.slateBg }}>
                {["Employee", "Current", "Change", `New gross / ${period}`, "Structure", "", ""].map((head, index) => (
                  <Mui.TableCell key={`${head}-${index}`} align={index === 1 || index === 2 ? "right" : "left"} sx={{ ...tableHeadSx, width: [undefined, 100, 80, 160, 160, 140, 44][index] }}>{head}</Mui.TableCell>
                ))}
              </Mui.TableRow>
            </Mui.TableHead>
            <Mui.TableBody>
              {visible.map((row) => {
                const employee = employeeOf(row.employee_id);
                const next = rowNewGross(form, employee, row);
                const structure = structureLabel(form, employee, row);
                const target = targetFor(form, templates, employee);
                const canCustomize = Boolean(next > 0 && templateById(templates, target.template_id) && versionOf(templates, target.template_id, target.version));
                return (
                  <Mui.TableRow key={row.employee_id} onClick={() => setPreviewId(row.employee_id)} sx={{ cursor: "pointer", backgroundColor: previewRow && sameId(previewRow.employee_id, row.employee_id) ? designColors.tealTint : undefined }}>
                    <Mui.TableCell sx={tableCellSx}>
                      <Mui.Typography sx={{ fontSize: 14, fontWeight: designWeights.semibold, color: designColors.slate900 }}>{employee.name}</Mui.Typography>
                      <Mui.Typography sx={designType.captionText}>{[employee.code, employeeLabel(templates, employee)].filter(Boolean).join(" · ")}</Mui.Typography>
                      {problemsLine(row)}
                    </Mui.TableCell>
                    <Mui.TableCell align="right" sx={tableCellSx}>{money(employee.gross)}</Mui.TableCell>
                    <Mui.TableCell align="right" sx={{ ...tableCellSx, color: next >= employee.gross ? "#067647" : "#DC2626", fontWeight: designWeights.semibold }}>{pct(employee.gross, next)}</Mui.TableCell>
                    <Mui.TableCell sx={tableCellSx} onClick={(event) => event.stopPropagation()}>{grossInput(employee, row)}</Mui.TableCell>
                    <Mui.TableCell sx={tableCellSx}><ToneChip label={structure.label} tone={structure.tone} /></Mui.TableCell>
                    <Mui.TableCell sx={tableCellSx} onClick={(event) => event.stopPropagation()}>
                      <Mui.Link
                        component="button"
                        type="button"
                        underline="hover"
                        disabled={busy || !canCustomize}
                        onClick={() => setRowCustomize({ employee_id: row.employee_id, gross: next, lines: rowLines(catalog, templates, form, employee, row, next) })}
                        sx={{ fontSize: 14, fontWeight: designWeights.semibold, whiteSpace: "nowrap", color: designColors.tealDark, "&:disabled": { color: designColors.slate400, cursor: "default" } }}
                      >
                        {row.lines ? "Edit structure" : "Customize"}
                      </Mui.Link>
                    </Mui.TableCell>
                    <Mui.TableCell sx={{ ...tableCellSx, px: 0.5 }} onClick={(event) => event.stopPropagation()}>
                      <Mui.IconButton size="small" disabled={busy} aria-label={`Remove ${employee.name}`} onClick={() => set({ rows: form.rows.filter((item) => !sameId(item.employee_id, row.employee_id)) })}><Close fontSize="small" /></Mui.IconButton>
                    </Mui.TableCell>
                  </Mui.TableRow>
                );
              })}
              {!form.rows.length ? <EmptyRow colSpan={7} text="No employees yet. Use Add employees." /> : null}
            </Mui.TableBody>
          </Mui.Table>
        </Mui.Box>
        <Mui.Typography sx={{ ...designType.captionText, mt: 1.5 }}>
          Showing {visible.length} of {form.rows.length}
          {form.rows.length > ROW_LIMIT ? (
            <> · <Mui.Link component="button" type="button" underline="hover" onClick={() => setShowAll((value) => !value)} sx={{ fontSize: 12, fontWeight: designWeights.semibold, color: designColors.tealDark }}>{showAll ? "Show less" : "Show all"}</Mui.Link></>
          ) : null}
        </Mui.Typography>
        {showErrors && check.errors.rows ? <Mui.Typography sx={{ ...designType.captionText, color: "#DC2626", mt: 1 }}>{check.errors.rows}</Mui.Typography> : null}
      </SalarySection>
    );
  }

  const previewOptions = rows.map((row) => {
    const employee = employeeOf(row.employee_id);
    const next = rowNewGross(form, employee, row);
    return { value: String(row.employee_id), label: isTemplate ? `${employee.name} · ${versionLabel(employee.version)} → ${versionLabel(form.target_version)}` : `${employee.name} · ${pct(employee.gross, next)}` };
  });
  const customizedKept = !isTemplate && form.structure === "keep" ? rows.filter((row) => isCustomized(employeeOf(row.employee_id))).length : 0;
  const customizedReplaced = rows.filter((row) => isCustomized(employeeOf(row.employee_id))).length;
  const leftOut = isTemplate ? form.rows.filter((row) => !row.selected && isCustomized(employeeOf(row.employee_id)) && !blockReason(employeeOf(row.employee_id), noOpen)).length : 0;
  const blockedRows = isTemplate ? form.rows.filter((row) => blockReason(employeeOf(row.employee_id), noOpen)).length : 0;
  const previewBefore = previewEmployee ? currentLines(templates, previewEmployee) : [];
  const previewAfter = previewTarget ? rowLines(catalog, templates, form, previewEmployee, previewRow, rowNewGross(form, previewEmployee, previewRow)) : [];

  const side = (
    <Mui.Stack spacing={2}>
      {isTemplate && template ? (
        <SalarySection title={`What changes in ${versionLabel(form.target_version)}`}>
          <Mui.Box component="ul" sx={{ m: 0, pl: 2.5, "& li": { fontSize: 14, color: designColors.slate700, mb: 0.75 } }}>
            {[...form.from_versions].sort((a, b) => b - a).map((from) => {
              const changes = versionChanges(catalog, templates, template.id, from, form.target_version);
              return (
                <li key={from}>
                  <b>From {versionLabel(from)}:</b>{" "}
                  {changes === null
                    ? "the version details aren't loaded"
                    : changes.length
                      ? changes.map((item) => (item.change === "Upgraded" ? `${item.name} upgraded (${item.before} → ${item.after})` : `${item.name} ${item.change.toLowerCase()}`)).join(", ")
                      : "no component changes"}
                </li>
              );
            })}
          </Mui.Box>
          <Mui.Typography sx={{ ...designType.captionText, mt: 1 }}>The balancing component keeps earnings equal to gross.</Mui.Typography>
        </SalarySection>
      ) : (
        <SalarySection title="Revision total">
          <Mui.Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            {[[`Current gross / ${period}`, money(totals.current)], [`New gross / ${period}`, money(totals.next)]].map(([labelText, value]) => (
              <Mui.Box key={labelText}>
                <Mui.Typography sx={designType.captionText}>{labelText}</Mui.Typography>
                <Mui.Typography sx={{ fontSize: 18, fontWeight: designWeights.heading, color: designColors.slate900 }}>{value}</Mui.Typography>
              </Mui.Box>
            ))}
            <Mui.Box>
              <Mui.Typography sx={designType.captionText}>Difference</Mui.Typography>
              <Mui.Typography sx={{ fontSize: 18, fontWeight: designWeights.heading, color: totals.next >= totals.current ? "#067647" : "#DC2626" }}>
                {totals.next >= totals.current ? "+" : "−"}{money(Math.abs(totals.next - totals.current))}
              </Mui.Typography>
            </Mui.Box>
          </Mui.Box>
        </SalarySection>
      )}
      <SalarySection
        title="Preview"
        action={
          previewOptions.length ? (
            <Mui.TextField size="small" select value={previewRow ? String(previewRow.employee_id) : ""} onChange={(event) => setPreviewId(event.target.value)} inputProps={{ "aria-label": "Preview employee" }} sx={{ ...inputSx, width: 230 }}>
              {previewOptions.map((option) => <Mui.MenuItem key={option.value} value={option.value}>{option.label}</Mui.MenuItem>)}
            </Mui.TextField>
          ) : null
        }
      >
        {previewEmployee && previewBefore.length && previewAfter.length ? (
          <CompareBreakdown
            catalog={catalog}
            beforeLines={previewBefore}
            beforeGross={previewEmployee.gross}
            afterLines={previewAfter}
            afterGross={rowNewGross(form, previewEmployee, previewRow)}
            beforeLabel={isTemplate ? versionLabel(previewEmployee.version) : "Current"}
            afterLabel={isTemplate ? versionLabel(form.target_version) : "New"}
          />
        ) : (
          <Mui.Box>
            {previewEmployee ? (
              <>
                <Mui.Stack direction="row" justifyContent="space-between" sx={{ py: 0.6 }}>
                  <Mui.Typography sx={{ fontSize: 14, color: designColors.slate700 }}>{previewEmployee.name} · current gross</Mui.Typography>
                  <Mui.Typography sx={{ fontSize: 14, color: designColors.slate900 }}>{money(previewEmployee.gross)}</Mui.Typography>
                </Mui.Stack>
                <Mui.Stack direction="row" justifyContent="space-between" sx={{ py: 0.6, borderTop: `1px solid ${designColors.slate200}` }}>
                  <Mui.Typography sx={{ fontSize: 14, fontWeight: designWeights.heading, color: designColors.slate700 }}>New gross</Mui.Typography>
                  <Mui.Typography sx={{ fontSize: 14, fontWeight: designWeights.heading, color: designColors.slate900 }}>{money(rowNewGross(form, previewEmployee, previewRow))}</Mui.Typography>
                </Mui.Stack>
                <Mui.Typography sx={{ ...designType.captionText, mt: 1 }}>
                  {!previewEmployee.template_id || templateById(templates, previewEmployee.template_id)
                    ? "Loading the component breakdown..."
                    : "The component breakdown isn't available: this employee's template wasn't found."}
                </Mui.Typography>
              </>
            ) : (
              <Mui.Typography sx={designType.helperText}>Add employees to see the breakdown.</Mui.Typography>
            )}
          </Mui.Box>
        )}
      </SalarySection>
      {count ? (
        <>
          {rows.some((row) => blockReason(employeeOf(row.employee_id), noOpen)) ? (
            <CheckLine ok={false}>Some employees can't be revised now (reason on each row)</CheckLine>
          ) : (
            <CheckLine ok>
              {isTemplate
                ? `${count} employee${count === 1 ? "" : "s"} move to ${versionLabel(form.target_version)}${form.change_mode === "none" ? " · gross unchanged" : ""}`
                : `${count} employee${count === 1 ? " has" : "s have"} an active salary · no open revision`}
            </CheckLine>
          )}
          <CheckLine ok={!check.errors.effective_from}>
            {check.errors.effective_from || (minDate ? `Starts ${formatDay(form.effective_from)}, after the last processed payroll` : `Starts ${formatDay(form.effective_from)}`)}
          </CheckLine>
          <CheckLine ok={check.earningsOk}>{check.earningsOk ? "Earnings add up to gross for everyone" : "Earnings don't add up to gross for some employees"}</CheckLine>
        </>
      ) : null}
      {customizedKept ? warning(`${customizedKept} customized employee${customizedKept === 1 ? " keeps" : "s keep"} their customization; only gross changes.`) : null}
      {!isTemplate && form.structure !== "keep" && customizedReplaced ? warning(`${customizedReplaced} customized employee${customizedReplaced === 1 ? " loses" : "s lose"} their customization.`) : null}
      {leftOut ? warning(`${leftOut} customized employee${leftOut === 1 ? "" : "s"} left out. Tick them only if their changes can be replaced.`) : null}
      {blockedRows ? warning(`${blockedRows} employee${blockedRows === 1 ? "" : "s"} can't be added (reason on each row).`) : null}
      <SalarySection title="Approval route">
        <ApprovalSteps request={null} />
        <Mui.Typography sx={{ ...designType.captionText, mt: 1.5 }}>One request for {count} employee{count === 1 ? "" : "s"}. Current salaries stay until L2 approves.</Mui.Typography>
      </SalarySection>
    </Mui.Stack>
  );

  const resubmit = revision?.status === "Rejected";
  return (
    <>
      <GlobalSidePanel
        open={open}
        onClose={busy ? undefined : onClose}
        expanded={expanded}
        onToggleExpand={onToggleExpand}
        breadcrumb={[...trail, revision ? revision.number : "New revision"]}
        header={
          <PanelTitleBlock
            icon={<TrendingUp />}
            title={`Revise Salary · ${isTemplate ? "From a template" : "Select employees"}`}
            chips={revision ? <ToneChip label={revision.status === "Draft" ? "Draft" : revision.number} tone={resubmit ? "red" : "slate"} /> : null}
            subtitle="Each revision creates a new salary version after approval."
          />
        }
        bodySx={panelBodySx}
        footer={
          <PanelFooter note={`${count} employee${count === 1 ? "" : "s"} · approved on submit${isTemplate ? " · template unchanged" : ""}`}>
            {revision?.status === "Draft" && onDelete ? <SecondaryButton onClick={() => onDelete(revision)} disabled={busy} sx={deleteButtonSx}>Delete draft</SecondaryButton> : null}
            <SecondaryButton onClick={saveDraft} disabled={busy}>Save draft</SecondaryButton>
            <PrimaryButton onClick={submit} disabled={busy}>{resubmit ? "Resubmit & approve" : "Submit & approve"}</PrimaryButton>
          </PanelFooter>
        }
      >
        <Mui.Box sx={{ display: "grid", gridTemplateColumns: expanded ? { xs: "1fr", lg: "minmax(0, 1fr) 400px" } : "1fr", gap: 2, alignItems: "start" }}>
          <Mui.Stack spacing={2} sx={{ minWidth: 0 }}>
            {resubmit ? (
              <RequestInfoBanner
                tone="danger"
                title={`Rejected at L${revision.rejection?.level ?? revision.level}${revision.rejection?.by ? ` by ${revision.rejection.by}` : ""}`}
                body={revision.rejection?.reason || "No reason given."}
              />
            ) : null}
            {showErrors && blocking ? <RequestInfoBanner tone="danger" body="Fix the highlighted fields before submitting." /> : null}
            {whatChanges}
            {employeesSection}
          </Mui.Stack>
          <Mui.Box sx={expanded ? { position: { lg: "sticky" }, top: { lg: 0 } } : undefined}>{side}</Mui.Box>
        </Mui.Box>
      </GlobalSidePanel>
      <SelectEmployeesPanel
        open={selecting}
        employees={employees}
        templates={templates}
        // Until the eligible list is in, no one can be matched to a salary.
        loading={eligibleLoading || (!eligibleLoaded && !eligibleError)}
        error={eligibleError}
        excludeIds={form.rows.map((row) => row.employee_id)}
        onClose={() => setSelecting(false)}
        onAdd={(ids) => {
          set({ rows: [...form.rows, ...ids.map((id) => ({ employee_id: id, new_gross: "", manual: false }))] });
          setSelecting(false);
        }}
      />
      {(() => {
        // One employee's structure at their new gross; Apply keeps it on the
        // row (and the gross the edited earnings add up to).
        const customizeRow = rowCustomize ? form.rows.find((row) => sameId(row.employee_id, rowCustomize.employee_id)) : null;
        const employee = customizeRow ? employeeOf(customizeRow.employee_id) : null;
        const target = employee ? targetFor(form, templates, employee) : null;
        const rowTemplate = target ? templateById(templates, target.template_id) : null;
        const rowVersion = target ? versionOf(templates, target.template_id, target.version) : null;
        return (
          <CustomizeStructurePanel
            open={Boolean(customizeRow && rowTemplate && rowVersion)}
            fullWidth={Boolean(expanded)}
            grossFollowsEarnings
            catalog={catalog}
            employee={employee}
            template={rowTemplate}
            version={rowVersion}
            row={rowCustomize}
            onClose={() => setRowCustomize(null)}
            onApply={(lines, gross) => {
              setRow(rowCustomize.employee_id, {
                lines: lines || null,
                manual: true,
                new_gross: String(gross > 0 ? gross : rowCustomize.gross),
              });
              setRowCustomize(null);
            }}
          />
        );
      })()}
      {template && templateLines.length ? (
        <CustomizeStructurePanel
          open={customizing}
          fullWidth
          catalog={catalog}
          employee={{ name: "everyone selected", code: "Common structure" }}
          template={template}
          version={currentTemplateVersion(template)}
          row={{ gross: previewEmployee ? rowNewGross(form, previewEmployee, previewRow) : 30000, lines: form.common_lines }}
          onClose={() => setCustomizing(false)}
          onApply={(lines) => {
            set({ common_lines: lines || templateLines.map((line) => ({ ...line })) });
            setCustomizing(false);
          }}
        />
      ) : null}
    </>
  );
};

// ---------------- Review ----------------

export const RevisionReviewPanel = ({
  open,
  revision,
  catalog,
  templates,
  canApprove,
  canCancel,
  loading,
  busy,
  expanded,
  onToggleExpand,
  onClose,
  onApprove,
  onReject,
  onCancel,
}) => {
  const trail = useSalaryPanelTrail(SCREEN, onClose, busy);
  const [openRow, setOpenRow] = useState(null);
  const [comment, setComment] = useState("");
  // null, "reject" or "cancel".
  const [mode, setMode] = useState(null);
  const [reason, setReason] = useState("");
  const [showReasonError, setShowReasonError] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const id = revision?.id;
  useEffect(() => {
    setOpenRow(null);
    setComment("");
    setMode(null);
    setReason("");
    setShowReasonError(false);
    setShowAll(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  if (!revision) return null;

  const status = requestStatus(revision);
  const count = revision.employees.length || revision.employee_count || 0;
  const pending = revision.status === "Pending";
  const change = changeText(revision, templates);
  const sum = (key) => revision.employees.reduce((total, row) => total + Number(row[key] || 0), 0);
  const current = revision.employees.length ? sum("current_gross") : revision.total_current ?? 0;
  const next = revision.employees.length ? sum("new_gross") : revision.total_new ?? 0;
  const shown = showAll ? revision.employees : revision.employees.slice(0, 8);
  const dotColor = (title) => (title.startsWith("Approved") ? "#12B76A" : title.startsWith("Rejected") || title.startsWith("Cancelled") ? "#DC2626" : title.startsWith("Submitted") || title.startsWith("Imported") || title.startsWith("Resubmitted") ? "#2878FF" : designColors.slate400);
  const switchMode = (value) => {
    setMode(value);
    setReason("");
    setShowReasonError(false);
  };
  const danger = { color: "#B42318", borderColor: "#FCA5A5" };

  let footer;
  if (mode === "reject") {
    footer = (
      <PanelFooter note={`Rejecting returns all ${count} employee${count === 1 ? "" : "s"}`}>
        <SecondaryButton onClick={() => switchMode(null)} disabled={busy}>Back</SecondaryButton>
        <PrimaryButton sx={dangerButtonSx} disabled={busy} onClick={() => { setShowReasonError(true); if (reason.trim()) onReject(revision, reason.trim()); }}>Reject request</PrimaryButton>
      </PanelFooter>
    );
  } else if (mode === "cancel") {
    footer = (
      <PanelFooter note="Only the person who raised it, before final approval">
        <SecondaryButton onClick={() => switchMode(null)} disabled={busy}>Back</SecondaryButton>
        <PrimaryButton sx={dangerButtonSx} disabled={busy} onClick={() => onCancel(revision, reason.trim())}>Cancel request</PrimaryButton>
      </PanelFooter>
    );
  } else if (pending && (canApprove || canCancel)) {
    footer = (
      <Mui.Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="space-between" sx={{ width: "100%" }}>
        <Mui.Stack direction="row" spacing={1.5}>
          {canApprove ? <SecondaryButton onClick={() => switchMode("reject")} disabled={busy} sx={danger}>Reject</SecondaryButton> : null}
          {canCancel ? <SecondaryButton onClick={() => switchMode("cancel")} disabled={busy} sx={danger}>Cancel request</SecondaryButton> : null}
        </Mui.Stack>
        <Mui.Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1.5}>
          <SecondaryButton onClick={onClose} disabled={busy}>Close</SecondaryButton>
          {canApprove ? <PrimaryButton onClick={() => onApprove(revision, comment.trim())} disabled={busy || loading}>Approve ({count} employee{count === 1 ? "" : "s"})</PrimaryButton> : null}
        </Mui.Stack>
      </Mui.Stack>
    );
  } else {
    footer = (
      <PanelFooter note={status.detail}>
        <SecondaryButton onClick={onClose}>Close</SecondaryButton>
      </PanelFooter>
    );
  }

  const structureChip = (row) => {
    const moved = (row.to.template_id && !sameId(row.from.template_id, row.to.template_id)) || (row.to.version && row.from.version !== row.to.version);
    if (moved) {
      const toName = row.to.template_name || templateById(templates, row.to.template_id)?.name || "";
      const fromName = row.from.template_name || templateById(templates, row.from.template_id)?.name || "";
      return <ToneChip label={`${toName && toName !== fromName ? `${toName} ` : ""}${versionLabel(row.from.version)} → ${versionLabel(row.to.version)}`} tone="blue" />;
    }
    return <ToneChip label={row.from.customized ? "Customized · kept" : "Template Linked"} tone={row.from.customized ? "purple" : "slate"} />;
  };
  const breakdown = (row) => {
    const before = row.current_lines || versionOf(templates, row.from.template_id, row.from.version)?.lines || [];
    const after = row.revised_lines || linesFor(templates, { ...row.to, lines: row.lines });
    if (!before.length && !after.length) return <Mui.Typography sx={designType.helperText}>The breakdown isn't available for this employee.</Mui.Typography>;
    return (
      <CompareBreakdown
        catalog={catalog}
        beforeLines={before}
        beforeGross={row.current_gross}
        beforeResult={row.current_result}
        afterLines={after.length ? after : before}
        afterGross={row.new_gross}
        afterResult={row.revised_result}
      />
    );
  };

  return (
    <GlobalSidePanel
      open={open}
      onClose={busy ? undefined : onClose}
      expanded={expanded}
      onToggleExpand={onToggleExpand}
      breadcrumb={[...trail, revision.number]}
      header={
        <PanelTitleBlock
          icon={<AssignmentTurnedInOutlined />}
          title={pending ? "Review salary revision" : "Salary revision"}
          chips={<ToneChip label={status.short} tone={status.tone} dot />}
          subtitle={[revision.number, revision.reason, `${count} employee${count === 1 ? "" : "s"}`, revision.submitted_at ? `submitted ${formatDay(revision.submitted_at)}` : null].filter(Boolean).join(" · ")}
        />
      }
      bodySx={panelBodySx}
      footer={footer}
    >
      <Mui.Stack spacing={2}>
        <SummaryStrip
          items={[
            { label: "Change", value: change.main },
            { label: "Structure", value: change.sub },
            { label: "Effective from", value: formatDay(revision.effective_from || revision.employees[0]?.effective_from) },
            { label: "New gross", value: `${money(next)} (${next >= current ? "+" : "−"}${money(Math.abs(next - current))})` },
          ]}
        />
        {revision.status === "Rejected" ? (
          <RequestInfoBanner
            tone="danger"
            title={`Rejected at L${revision.rejection?.level ?? revision.level}${revision.rejection?.by ? ` by ${revision.rejection.by}` : ""}`}
            body={revision.rejection?.reason || "No reason given."}
          />
        ) : null}
        {revision.comment ? <RequestInfoBanner title="Comment from the submitter" body={revision.comment} /> : null}
        <SalarySection title="Approval progress"><ApprovalSteps request={revision} /></SalarySection>
        <SalarySection title={`Employees · ${count}`}>
          {loading && !revision.details_loaded ? (
            <Mui.Typography sx={designType.helperText}>Loading employees...</Mui.Typography>
          ) : (
            <Mui.Box sx={{ mx: -2, my: -1.75 }}>
              <SimpleTable
                minWidth={640}
                heads={[{ label: "Employee" }, { label: "Current", align: "right", width: 100 }, { label: "New", align: "right", width: 110 }, { label: "Effective from", width: 120 }, { label: "Structure", width: 170 }, { label: "", width: 100 }]}
              >
                {shown.map((row) => {
                  const isOpen = openRow === row.employee_id;
                  return (
                    <React.Fragment key={row.employee_id}>
                      <Mui.TableRow sx={{ backgroundColor: isOpen ? designColors.slateBg : undefined }}>
                        <Mui.TableCell sx={tableCellSx}>
                          <Mui.Typography sx={{ fontSize: 14, fontWeight: designWeights.semibold, color: designColors.slate900 }}>{row.name || `Employee ${row.employee_id}`}</Mui.Typography>
                          <Mui.Typography sx={designType.captionText}>{[row.code, row.department].filter(Boolean).join(" · ")}</Mui.Typography>
                        </Mui.TableCell>
                        <Mui.TableCell align="right" sx={tableCellSx}>{money(row.current_gross)}</Mui.TableCell>
                        <Mui.TableCell align="right" sx={{ ...tableCellSx, fontWeight: designWeights.semibold }}>
                          {money(row.new_gross)}
                          <Mui.Typography sx={{ ...designType.captionText, color: row.new_gross >= row.current_gross ? "#067647" : "#DC2626" }}>{pct(row.current_gross, row.new_gross)}</Mui.Typography>
                        </Mui.TableCell>
                        <Mui.TableCell sx={tableCellSx}>{formatDay(row.effective_from || revision.effective_from)}</Mui.TableCell>
                        <Mui.TableCell sx={tableCellSx}>{structureChip(row)}</Mui.TableCell>
                        <Mui.TableCell align="right" sx={tableCellSx}>
                          <Mui.Link component="button" type="button" underline="hover" onClick={() => setOpenRow(isOpen ? null : row.employee_id)} sx={{ fontSize: 14, fontWeight: designWeights.semibold, color: designColors.tealDark }}>{isOpen ? "Hide" : "Breakdown"}</Mui.Link>
                        </Mui.TableCell>
                      </Mui.TableRow>
                      {isOpen ? (
                        <Mui.TableRow sx={{ backgroundColor: designColors.slateBg }}>
                          <Mui.TableCell colSpan={6} sx={tableCellSx}>
                            <Mui.Box sx={{ border: `1px solid ${designColors.slate200}`, borderRadius: designRadii.card, p: 2, backgroundColor: designColors.white }}>
                              {breakdown(row)}
                            </Mui.Box>
                          </Mui.TableCell>
                        </Mui.TableRow>
                      ) : null}
                    </React.Fragment>
                  );
                })}
                {!revision.employees.length ? <EmptyRow colSpan={6} text="No employee details." /> : null}
              </SimpleTable>
            </Mui.Box>
          )}
          {revision.employees.length > 8 ? (
            <Mui.Typography sx={{ ...designType.captionText, mt: 1.5 }}>
              Showing {shown.length} of {revision.employees.length} ·{" "}
              <Mui.Link component="button" type="button" underline="hover" onClick={() => setShowAll((value) => !value)} sx={{ fontSize: 12, fontWeight: designWeights.semibold, color: designColors.tealDark }}>{showAll ? "Show less" : "Show all"}</Mui.Link>
            </Mui.Typography>
          ) : null}
        </SalarySection>
        <SalarySection title="History">
          <Mui.Box sx={{ mx: -2, my: -1.75 }}>
            {revision.history.length ? (
              revision.history.map((item, index) => (
                <Mui.Stack key={`${item.title}-${index}`} direction="row" spacing={1.5} sx={{ px: 2, py: 1.5, borderTop: index ? `1px solid ${designColors.slate200}` : "none" }}>
                  <Mui.Box sx={{ width: 10, height: 10, mt: 0.75, borderRadius: "50%", backgroundColor: dotColor(item.title), flexShrink: 0 }} />
                  <Mui.Box>
                    <Mui.Typography sx={{ fontSize: 14, color: designColors.slate900 }}><b>{item.title}</b>{item.by ? ` by ${item.by}` : ""}</Mui.Typography>
                    <Mui.Typography sx={designType.captionText}>{formatStamp(item.at)}{item.note ? ` · "${item.note}"` : ""}</Mui.Typography>
                  </Mui.Box>
                </Mui.Stack>
              ))
            ) : (
              <Mui.Typography sx={{ ...designType.helperText, px: 2, py: 1.5 }}>{loading ? "Loading history..." : "No history yet."}</Mui.Typography>
            )}
          </Mui.Box>
        </SalarySection>
        {pending && canApprove && !mode ? (
          <Field label={<>Comment <Mui.Box component="span" sx={{ color: designColors.slate500 }}>· optional</Mui.Box></>}>
            <Mui.TextField size="small" value={comment} onChange={(event) => setComment(event.target.value)} multiline minRows={2} placeholder="Add a note for the history" disabled={busy} sx={multilineFieldSx} />
          </Field>
        ) : null}
        {mode === "reject" ? (
          <SalarySection title="Reject this request" tone="danger">
            <Mui.Typography sx={{ fontSize: 14, color: designColors.slate700, mb: 1.5 }}>
              The whole request ({count} employee{count === 1 ? "" : "s"}) goes back to the submitter as <b>Rejected</b>. Current salaries stay as they are.
            </Mui.Typography>
            <Field label={<>Reason<Required /></>} helper="Shown to the submitter and kept in the history" error={showReasonError && !reason.trim() ? "Enter a reason" : undefined}>
              <Mui.TextField size="small" value={reason} onChange={(event) => setReason(event.target.value)} multiline minRows={3} placeholder="Why is this revision being rejected?" error={showReasonError && !reason.trim()} disabled={busy} sx={multilineFieldSx} />
            </Field>
          </SalarySection>
        ) : null}
        {mode === "cancel" ? (
          <SalarySection title="Cancel this request" tone="danger">
            <Mui.Typography sx={{ fontSize: 14, color: designColors.slate700, mb: 1.5 }}>
              The revision is withdrawn for all {count} employee{count === 1 ? "" : "s"}. Their current salaries stay as they are.
            </Mui.Typography>
            <Field label={<>Reason <Mui.Box component="span" sx={{ color: designColors.slate500 }}>· optional</Mui.Box></>} helper="Kept in the history">
              <Mui.TextField size="small" value={reason} onChange={(event) => setReason(event.target.value)} multiline minRows={3} placeholder="e.g. Raised by mistake" disabled={busy} sx={multilineFieldSx} />
            </Field>
          </SalarySection>
        ) : null}
      </Mui.Stack>
    </GlobalSidePanel>
  );
};
